import React, { useState } from 'react';
import { FileText, Download, ExternalLink, Star, Clock, CheckCircle2, AlertTriangle, Loader2, Mail, MailCheck, BookOpen, MessageCircle, ChevronRight, Sparkles, Filter, X, Send } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { ZodiacTheme } from './zodiacThemes';
interface ReportsTabProps {
  theme: ZodiacTheme;
  onNavigate: (page: string) => void;
}
interface Report {
  id: string;
  title: string;
  type: 'natal' | 'solar' | 'compatibility' | 'forecast' | 'combo';
  date: string;
  status: 'generating' | 'ready' | 'error';
  pages: number;
  emailSent: boolean;
  reviewed: boolean;
  rating: number | null;
  orderId: string;
  progress?: number;
}
const typeConfig: Record<string, {
  label: string;
  color: string;
  bg: string;
  icon: string;
}> = {
  natal: {
    label: 'Натальная карта',
    color: 'text-indigo-700',
    bg: 'bg-indigo-50',
    icon: '🌟'
  },
  solar: {
    label: 'Соляр',
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    icon: '☀️'
  },
  compatibility: {
    label: 'Совместимость',
    color: 'text-rose-700',
    bg: 'bg-rose-50',
    icon: '💞'
  },
  forecast: {
    label: 'Прогноз',
    color: 'text-teal-700',
    bg: 'bg-teal-50',
    icon: '🔮'
  },
  combo: {
    label: 'Комбо-анализ',
    color: 'text-purple-700',
    bg: 'bg-purple-50',
    icon: '✨'
  }
};
const statusConfig: Record<string, {
  label: string;
  variant: 'success' | 'warning' | 'error';
  icon: React.ReactNode;
}> = {
  generating: {
    label: 'Генерируется...',
    variant: 'warning',
    icon: <Loader2 className="w-3.5 h-3.5 animate-spin" data-id="element-17961" />
  },
  ready: {
    label: 'Готов',
    variant: 'success',
    icon: <CheckCircle2 className="w-3.5 h-3.5" data-id="element-17962" />
  },
  error: {
    label: 'Ошибка',
    variant: 'error',
    icon: <AlertTriangle className="w-3.5 h-3.5" data-id="element-17963" />
  }
};
export function ReportsTab({
  theme,
  onNavigate
}: ReportsTabProps) {
  const [filterType, setFilterType] = useState<string>('all');
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submittedReviews, setSubmittedReviews] = useState<string[]>([]);
  const reports: Report[] = [{
    id: 'RPT-001',
    title: 'Натальная карта (Анна)',
    type: 'natal',
    date: '12 Окт 2024',
    status: 'ready',
    pages: 42,
    emailSent: true,
    reviewed: false,
    rating: null,
    orderId: 'ORD-1201'
  }, {
    id: 'RPT-002',
    title: 'Соляр 2024 (Анна)',
    type: 'solar',
    date: '10 Окт 2024',
    status: 'ready',
    pages: 36,
    emailSent: true,
    reviewed: true,
    rating: 5,
    orderId: 'ORD-1198'
  }, {
    id: 'RPT-003',
    title: 'Совместимость (Анна + Иван)',
    type: 'compatibility',
    date: '15 Сен 2024',
    status: 'ready',
    pages: 58,
    emailSent: false,
    reviewed: false,
    rating: null,
    orderId: 'ORD-1185'
  }, {
    id: 'RPT-004',
    title: 'Прогноз на Ноябрь 2024',
    type: 'forecast',
    date: '01 Ноя 2024',
    status: 'generating',
    pages: 0,
    emailSent: false,
    reviewed: false,
    rating: null,
    orderId: 'ORD-1220',
    progress: 68
  }, {
    id: 'RPT-005',
    title: 'Комбо-анализ (Анна)',
    type: 'combo',
    date: '20 Авг 2024',
    status: 'ready',
    pages: 62,
    emailSent: true,
    reviewed: true,
    rating: 4,
    orderId: 'ORD-1150'
  }];
  const filtered = filterType === 'all' ? reports : reports.filter(r => r.type === filterType);
  const readyCount = reports.filter(r => r.status === 'ready').length;
  const totalPages = reports.filter(r => r.status === 'ready').reduce((sum, r) => sum + r.pages, 0);
  const unreviewed = reports.filter(r => r.status === 'ready' && !r.reviewed && !submittedReviews.includes(r.id));
  const handleSubmitReview = (reportId: string) => {
    if (reviewRating === 0) return;
    setSubmittedReviews(prev => [...prev, reportId]);
    setReviewingId(null);
    setReviewRating(0);
    setReviewText('');
  };
  return <div className="space-y-6" data-id="element-17964">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-17965">
        <div data-id="element-17966">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-17967">Мои отчёты</h2>
          <p className="text-sm text-slate-500 mt-1" data-id="element-17968">
            {readyCount} отчётов готово • {totalPages} страниц суммарно
          </p>
        </div>
        <Button onClick={() => onNavigate('home')} icon={<Sparkles className="w-4 h-4" data-id="element-17970" />} data-id="element-17969">
          Заказать новый
        </Button>
      </div>

      {/* Review Banner */}
      {unreviewed.length > 0 && <div className="flex items-center gap-4 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-17971">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0" data-id="element-17972">
            <MessageCircle className="w-5 h-5 text-amber-600" data-id="element-17973" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-17974">
            <p className="text-sm font-medium text-amber-900" data-id="element-17975">
              {unreviewed.length === 1 ? 'Оцените ваш отчёт' : `Оцените ${unreviewed.length} отчёта`}{' '}
              — получите скидку 10%
            </p>
            <p className="text-xs text-amber-700 mt-0.5" data-id="element-17976">
              Ваш отзыв помогает улучшить качество AI-генерации
            </p>
          </div>
          <button onClick={() => setReviewingId(unreviewed[0].id)} className="shrink-0 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-lg transition-colors" data-id="element-17977">
            Оценить
          </button>
        </div>}

      {/* Filters */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1" data-id="element-17978">
        <button onClick={() => setFilterType('all')} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors shrink-0 ${filterType === 'all' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-17979">
          Все ({reports.length})
        </button>
        {Object.entries(typeConfig).map(([key, cfg]) => {
        const count = reports.filter(r => r.type === key).length;
        if (count === 0) return null;
        return <button key={key} onClick={() => setFilterType(key)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors shrink-0 ${filterType === key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-17980">
              {cfg.icon} {cfg.label} ({count})
            </button>;
      })}
      </div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-17981">
        {filtered.map(report => {
        const tc = typeConfig[report.type];
        const sc = statusConfig[report.status];
        const isReviewing = reviewingId === report.id;
        const wasReviewed = report.reviewed || submittedReviews.includes(report.id);
        return <Card key={report.id} padding="none" className="overflow-hidden border-slate-200 hover:shadow-md transition-shadow" data-id="element-17982">
              {/* Card Header */}
              <div className="p-4 pb-3" data-id="element-17983">
                <div className="flex justify-between items-start mb-3" data-id="element-17984">
                  <div className={`w-11 h-11 rounded-xl ${tc.bg} flex items-center justify-center text-lg`} data-id="element-17985">
                    {tc.icon}
                  </div>
                  <div className="flex items-center gap-2" data-id="element-17986">
                    {report.emailSent && <span className="flex items-center gap-1 text-[10px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full" data-id="element-17987">
                        <MailCheck className="w-3 h-3" data-id="element-17988" /> На почте
                      </span>}
                    <Badge variant={sc.variant} size="sm" className="text-[10px] flex items-center gap-1" data-id="element-17989">
                      {sc.icon} {sc.label}
                    </Badge>
                  </div>
                </div>

                <h3 className="font-bold text-slate-900 text-sm mb-1" data-id="element-17990">
                  {report.title}
                </h3>

                <div className="flex items-center gap-3 text-xs text-slate-500" data-id="element-17991">
                  <span className="flex items-center gap-1" data-id="element-17992">
                    <Clock className="w-3 h-3" data-id="element-17993" /> {report.date}
                  </span>
                  {report.status === 'ready' && <span className="flex items-center gap-1" data-id="element-17994">
                      <BookOpen className="w-3 h-3" data-id="element-17995" /> {report.pages} стр.
                    </span>}
                  {wasReviewed && <span className="flex items-center gap-1 text-amber-600" data-id="element-17996">
                      <Star className="w-3 h-3 fill-amber-500" data-id="element-17997" />{' '}
                      {report.rating || '5'}/5
                    </span>}
                </div>

                {/* Generating Progress */}
                {report.status === 'generating' && report.progress !== undefined && <div className="mt-3" data-id="element-17998">
                      <div className="flex justify-between text-[10px] text-slate-500 mb-1" data-id="element-17999">
                        <span data-id="element-18000">Генерация отчёта...</span>
                        <span data-id="element-18001">{report.progress}%</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-1.5" data-id="element-18002">
                        <div className="bg-indigo-500 h-1.5 rounded-full transition-all duration-500" style={{
                  width: `${report.progress}%`
                }} data-id="element-18003" />
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1.5" data-id="element-18004">
                        Примерное время: ~3 минуты
                      </p>
                    </div>}
              </div>

              {/* Actions */}
              {report.status === 'ready' && <div className="px-4 pb-4 flex flex-col gap-2" data-id="element-18005">
                  <div className="flex gap-2" data-id="element-18006">
                    <button onClick={() => onNavigate('full-report')} className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-lg transition-colors" data-id="element-18007">
                      <ExternalLink className="w-3.5 h-3.5" data-id="element-18008" /> Открыть онлайн
                    </button>
                    <button className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg transition-colors" data-id="element-18009">
                      <Download className="w-3.5 h-3.5" data-id="element-18010" /> Скачать PDF
                    </button>
                  </div>

                  {!report.emailSent && <button className="flex items-center justify-center gap-2 px-3 py-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 text-xs font-medium rounded-lg transition-colors" data-id="element-18011">
                      <Mail className="w-3.5 h-3.5" data-id="element-18012" /> Отправить на почту
                    </button>}

                  {/* Review prompt */}
                  {!wasReviewed && !isReviewing && <button onClick={() => {
              setReviewingId(report.id);
              setReviewRating(0);
              setReviewText('');
            }} className="flex items-center justify-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 hover:bg-amber-100 text-amber-700 text-xs font-medium rounded-lg transition-colors" data-id="element-18013">
                      <Star className="w-3.5 h-3.5" data-id="element-18014" /> Оценить отчёт — скидка
                      10%
                    </button>}
                </div>}

              {report.status === 'error' && <div className="px-4 pb-4" data-id="element-18015">
                  <button className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-rose-50 border border-rose-200 hover:bg-rose-100 text-rose-700 text-xs font-medium rounded-lg transition-colors" data-id="element-18016">
                    <AlertTriangle className="w-3.5 h-3.5" data-id="element-18017" /> Повторить
                    генерацию
                  </button>
                </div>}

              {/* Inline Review Form */}
              {isReviewing && <div className="px-4 pb-4 border-t border-slate-100 pt-4 space-y-3 animate-in fade-in slide-in-from-bottom-2" data-id="element-18018">
                  <div className="flex items-center justify-between" data-id="element-18019">
                    <p className="text-xs font-medium text-slate-700" data-id="element-18020">
                      Оцените отчёт
                    </p>
                    <button onClick={() => setReviewingId(null)} className="p-1 text-slate-400 hover:text-slate-600" data-id="element-18021">
                      <X className="w-4 h-4" data-id="element-18022" />
                    </button>
                  </div>

                  {/* Stars */}
                  <div className="flex gap-1" data-id="element-18023">
                    {[1, 2, 3, 4, 5].map(s => <button key={s} onMouseEnter={() => setReviewHover(s)} onMouseLeave={() => setReviewHover(0)} onClick={() => setReviewRating(s)} className="p-0.5 transition-transform hover:scale-110" aria-label={`${s} из 5`} data-id="element-18024">
                        <Star className={`w-6 h-6 transition-colors ${s <= (reviewHover || reviewRating) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} data-id="element-18025" />
                      </button>)}
                  </div>

                  {reviewRating > 0 && <>
                      <textarea value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder={reviewRating >= 4 ? 'Что понравилось?' : 'Что можно улучшить?'} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 resize-none" rows={2} data-id="element-18026" />
                      <button onClick={() => handleSubmitReview(report.id)} className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-lg transition-colors" data-id="element-18027">
                        <Send className="w-3.5 h-3.5" data-id="element-18028" /> Отправить отзыв
                      </button>
                    </>}
                </div>}

              {/* Submitted Review Confirmation */}
              {submittedReviews.includes(report.id) && !report.reviewed && <div className="px-4 pb-4 border-t border-slate-100 pt-3" data-id="element-18029">
                  <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 p-2.5 rounded-lg" data-id="element-18030">
                    <CheckCircle2 className="w-4 h-4 shrink-0" data-id="element-18031" />
                    <span data-id="element-18032">
                      Спасибо! Скидка 10% на следующий отчёт активирована.
                    </span>
                  </div>
                </div>}
            </Card>;
      })}
      </div>

      {/* Empty State */}
      {filtered.length === 0 && <div className="text-center py-16" data-id="element-18033">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4" data-id="element-18034">
            <FileText className="w-8 h-8 text-slate-400" data-id="element-18035" />
          </div>
          <h3 className="font-bold text-slate-900 mb-2" data-id="element-18036">Нет отчётов</h3>
          <p className="text-sm text-slate-500 mb-6" data-id="element-18037">
            Закажите ваш первый AI-отчёт
          </p>
          <Button onClick={() => onNavigate('home')} data-id="element-18038">Заказать отчёт</Button>
        </div>}

      {/* Stats Footer */}
      <div className="grid grid-cols-3 gap-3" data-id="element-18039">
        <div className="bg-slate-50 rounded-xl p-4 text-center" data-id="element-18040">
          <div className="text-2xl font-bold text-slate-900" data-id="element-18041">{readyCount}</div>
          <div className="text-xs text-slate-500 mt-1" data-id="element-18042">Отчётов готово</div>
        </div>
        <div className="bg-slate-50 rounded-xl p-4 text-center" data-id="element-18043">
          <div className="text-2xl font-bold text-slate-900" data-id="element-18044">{totalPages}</div>
          <div className="text-xs text-slate-500 mt-1" data-id="element-18045">Страниц суммарно</div>
        </div>
        <div className="bg-slate-50 rounded-xl p-4 text-center" data-id="element-18046">
          <div className="text-2xl font-bold text-amber-600" data-id="element-18047">
            {reports.filter(r => r.reviewed || submittedReviews.includes(r.id)).length}
            /{readyCount}
          </div>
          <div className="text-xs text-slate-500 mt-1" data-id="element-18048">Оценено</div>
        </div>
      </div>
    </div>;
}